/**
 * VersionValidator
 * Validates version-related data and operations
 * 
 * Skills:
 * - VALIDATE_VERSION_FORMAT: Ensures SHA/timestamp is valid format
 * - VALIDATE_POLL_INTERVAL: Ensures interval is reasonable
 * - VALIDATE_STORAGE: Ensures localStorage is accessible
 */

class VersionValidator {
  constructor() {
    this.MIN_POLL_INTERVAL = 60000; // 1 minute minimum
    this.MAX_POLL_INTERVAL = 3600000; // 1 hour maximum
    this.SHA_REGEX = /^[a-f0-9]{40}$/i;
    this.TIMESTAMP_REGEX = /^\d{13}$/; // 13-digit timestamp
  }

  /**
   * VALIDATE_VERSION_FORMAT
   * Validates that a version string is in the expected format
   * @param {string} version - The version string to validate
   * @returns {{ valid: boolean, type: 'sha' | 'timestamp' | 'unknown', error?: string }}
   */
  VALIDATE_VERSION_FORMAT(version) {
    if (!version) {
      return {
        valid: false,
        type: 'unknown',
        error: 'Version is required'
      };
    }

    if (typeof version !== 'string') {
      return {
        valid: false,
        type: 'unknown',
        error: 'Version must be a string'
      };
    }

    // Check if it's a valid SHA
    if (this.SHA_REGEX.test(version)) {
      return {
        valid: true,
        type: 'sha'
      };
    }

    // Check if it's a valid timestamp
    if (this.TIMESTAMP_REGEX.test(version)) {
      const timestamp = parseInt(version, 10);
      const now = Date.now();
      const oneYearAgo = now - (365 * 24 * 60 * 60 * 1000);
      const oneYearFromNow = now + (365 * 24 * 60 * 60 * 1000);

      if (timestamp >= oneYearAgo && timestamp <= oneYearFromNow) {
        return {
          valid: true,
          type: 'timestamp'
        };
      } else {
        return {
          valid: false,
          type: 'timestamp',
          error: 'Timestamp is out of reasonable range'
        };
      }
    }

    return {
      valid: false,
      type: 'unknown',
      error: 'Version format not recognized (expected SHA or timestamp)'
    };
  }

  /**
   * VALIDATE_POLL_INTERVAL
   * Validates that a polling interval is within acceptable bounds
   * @param {number} interval - The interval in milliseconds
   * @returns {{ valid: boolean, adjusted?: number, error?: string }}
   */
  VALIDATE_POLL_INTERVAL(interval) {
    if (typeof interval !== 'number') {
      return {
        valid: false,
        error: 'Interval must be a number'
      };
    }

    if (isNaN(interval) || !isFinite(interval)) {
      return {
        valid: false,
        error: 'Interval must be a valid number'
      };
    }

    if (interval < this.MIN_POLL_INTERVAL) {
      return {
        valid: false,
        adjusted: this.MIN_POLL_INTERVAL,
        error: `Interval too short. Minimum is ${this.MIN_POLL_INTERVAL}ms (1 minute)`
      };
    }

    if (interval > this.MAX_POLL_INTERVAL) {
      return {
        valid: false,
        adjusted: this.MAX_POLL_INTERVAL,
        error: `Interval too long. Maximum is ${this.MAX_POLL_INTERVAL}ms (1 hour)`
      };
    }

    return {
      valid: true
    };
  }

  /**
   * VALIDATE_STORAGE
   * Validates that localStorage is accessible and functioning
   * @returns {{ valid: boolean, error?: string, details?: object }}
   */
  VALIDATE_STORAGE() {
    const testKey = '__version_validator_test__';
    const testValue = 'test_' + Date.now();

    try {
      // Check if localStorage exists
      if (typeof localStorage === 'undefined') {
        return {
          valid: false,
          error: 'localStorage is not available',
          details: { reason: 'undefined' }
        };
      }

      // Try to write
      localStorage.setItem(testKey, testValue);

      // Try to read
      const retrieved = localStorage.getItem(testKey);
      if (retrieved !== testValue) {
        return {
          valid: false,
          error: 'localStorage read/write mismatch',
          details: { expected: testValue, received: retrieved }
        };
      }

      // Try to remove
      localStorage.removeItem(testKey);

      // Verify removal
      const afterRemove = localStorage.getItem(testKey);
      if (afterRemove !== null) {
        return {
          valid: false,
          error: 'localStorage remove failed',
          details: { stillPresent: true }
        };
      }

      return {
        valid: true,
        details: {
          available: true,
          readable: true,
          writable: true,
          removable: true
        }
      };
    } catch (error) {
      return {
        valid: false,
        error: `localStorage error: ${error.message}`,
        details: {
          errorName: error.name,
          errorMessage: error.message
        }
      };
    }
  }

  /**
   * VALIDATE_API_RESPONSE
   * Validates the response from the version API
   * @param {object} response - The API response object
   * @returns {{ valid: boolean, error?: string }}
   */
  VALIDATE_API_RESPONSE(response) {
    if (!response) {
      return {
        valid: false,
        error: 'Response is null or undefined'
      };
    }

    if (typeof response !== 'object') {
      return {
        valid: false,
        error: 'Response must be an object'
      };
    }

    if (!response.version) {
      return {
        valid: false,
        error: 'Response missing version field'
      };
    }

    const versionValidation = this.VALIDATE_VERSION_FORMAT(response.version);
    if (!versionValidation.valid) {
      return {
        valid: false,
        error: `Invalid version format: ${versionValidation.error}`
      };
    }

    if (response.timestamp && typeof response.timestamp !== 'number') {
      return {
        valid: false,
        error: 'Response timestamp must be a number'
      };
    }

    return {
      valid: true
    };
  }

  /**
   * RUN_ALL_VALIDATIONS
   * Runs all validation checks
   * @param {object} params - Parameters to validate
   * @returns {{ allValid: boolean, results: object }}
   */
  RUN_ALL_VALIDATIONS(params = {}) {
    const results = {
      storage: this.VALIDATE_STORAGE(),
      pollInterval: params.pollInterval 
        ? this.VALIDATE_POLL_INTERVAL(params.pollInterval) 
        : { valid: true, skipped: true },
      version: params.version 
        ? this.VALIDATE_VERSION_FORMAT(params.version) 
        : { valid: true, skipped: true },
      apiResponse: params.apiResponse 
        ? this.VALIDATE_API_RESPONSE(params.apiResponse) 
        : { valid: true, skipped: true }
    };

    const allValid = Object.values(results).every(r => r.valid);

    return {
      allValid,
      results
    };
  }
}

export default VersionValidator;
